<template>
  <modal-wrapper
    v-bind="$attrs"
    width="90%"
    height="auto"
    :scrollable="true"
    :name="name"
    @before-open="beforeOpen"
    @before-close="beforeClose"
    @close="close"
  >
    <template #content>
      <div
        v-if="!isLoading && hotel"
        class="hotel-suppliers"
      >
        <header class="hotel-suppliers__header">
          <div class="row">
            <div class="col">
              <div class="subtitle">
                Behind the scenes
              </div>
              <h2 class="title">
                {{ hotel.displayName }}
              </h2>
            </div>
            <div class="col">
              <multiselect
                v-model="supplierModel"
                label="title"
                track-by="id"
                :options="supplierOptions"
                hide-selected
                :allow-empty="false"
                :show-labels="false"
                class="select-suppliers"
              />
            </div>
          </div>
          <div class="tabs">
            <ul class="tabs__list">
              <li
                class="tabs__item"
                :class="{
                  'is-active': activeTab === 'photos',
                }"
              >
                <a
                  href="#"
                  @click.prevent="activeTab = 'photos'"
                >Photos</a>
              </li>
              <li
                class="tabs__item"
                :class="{
                  'is-active': activeTab === 'descriptions',
                }"
              >
                <a
                  href="#"
                  @click.prevent="activeTab = 'descriptions'"
                >Descriptions</a>
              </li>
              <li
                class="tabs__item"
                :class="{
                  'is-active': activeTab === 'facilities',
                }"
              >
                <a
                  href="#"
                  @click.prevent="activeTab = 'facilities'"
                >Facilities</a>
              </li>
            </ul>
          </div>
        </header>
        <div class="hotel-suppliers__content">
          <div
            v-if="activeTab === 'photos'"
            class="tabs-panel"
          >
            <div class="hotel-photos cols-compare">
              <div class="cols-compare__col">
                <h3 class="col-title">
                  Best Practice
                </h3>
                <div class="col-content">
                  <images-grid
                    :images="images.map(image => ({
                      src: image.imageUrl,
                      alt: image.description,
                    }))"
                    @click="showLightbox($refs.lightboxBestPractice, $event.index)"
                  />
                  <light-box
                    ref="lightboxBestPractice"
                    :show-caption="true"
                    :show-light-box="false"
                    :media="images.map(image => ({
                      thumb: image.imageUrl,
                      src: image.imageUrl,
                      caption: image.description,
                    }))"
                  />
                </div>
              </div>
              <div class="cols-compare__col">
                <h3 class="col-title">
                  Provided by Supplier
                </h3>
                <div class="col-content">
                  <images-grid
                    :images="supplierImages.map(image => ({
                      src: image,
                      alt: 'Hotel Photo',
                    }))"
                    @click="showLightbox($refs.lightboxBySupplier, $event.index)"
                  />
                  <light-box
                    ref="lightboxBySupplier"
                    :show-light-box="false"
                    :media="supplierImages.map(image => ({
                      thumb: image,
                      src: image,
                    }))"
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="activeTab === 'descriptions'"
            class="tabs-panel"
          >
            <multiselect
              v-model="language"
              label="title"
              track-by="id"
              :options="languageOptions"
              :option-height="104"
              hide-selected
              :searchable="false"
              :allow-empty="false"
              :show-labels="false"
              class="select-language"
            >
              <template
                slot="singleLabel"
                slot-scope="props"
              >
                <span class="option__image">
                  <img
                    :src="props.option.img"
                    :alt="props.option.title"
                    width="16"
                    height="16"
                  >
                </span>
                <span class="option__title">{{ props.option.title }}</span>
              </template>
              <template
                slot="option"
                slot-scope="props"
              >
                <span class="option__image">
                  <img
                    :src="props.option.img"
                    :alt="props.option.title"
                    width="16"
                    height="16"
                  >
                </span>
                <span class="option__title">{{ props.option.title }}</span>
              </template>
            </multiselect>
            <div
              class="hotel-descriptions cols-compare"
              :class="{
                [`lang-${language.id}`]: true,
              }"
            >
              <div class="cols-compare__col">
                <h3 class="col-title">
                  Best Practice
                </h3>
                <div
                  class="col-content"
                  v-html="getDescription(language.id)"
                />
              </div>
              <div class="cols-compare__col">
                <h3 class="col-title">
                  Provided by Supplier
                </h3>
                <div
                  v-if="getDescription(language.id, true)"
                  class="col-content"
                  v-html="getDescription(language.id, true)"
                />
                <div
                  v-else
                  class="col-content"
                >
                  <p>Not Provided</p>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="activeTab === 'facilities'"
            class="tabs-panel"
          >
            <div class="hotel-facilities cols-compare">
              <div class="cols-compare__col">
                <h3 class="col-title">
                  Best Practice
                </h3>
                <div class="col-content">
                  <facilities-group
                    class="facilities-group"
                    :facilities="facilities.map(({ label }) => ({ name: label }))"
                  />
                </div>
              </div>
              <div class="cols-compare__col">
                <h3 class="col-title">
                  Provided by Supplier
                </h3>
                <div class="col-content">
                  <facilities-group
                    class="facilities-group"
                    :facilities="supplierFacilities.map(facility => ({ name: facility }))"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </modal-wrapper>
</template>
<script>
import { mapActions } from 'vuex';
import LightBox from 'vue-image-lightbox';
import ModalWrapper from '@/components/modals/ModalWrapper';
import ImagesGrid from '@/components/ImagesGrid';
import FacilitiesGroup from '@/components/FacilitiesGroup';
import { random, sortBy } from 'lodash';

import { languageOptions } from '@/helpers';

require('vue-image-lightbox/dist/vue-image-lightbox.min.css');

const name = 'modal-hotel-suppliers';

export default {
  name,
  components: {
    LightBox,
    ModalWrapper,
    ImagesGrid,
    FacilitiesGroup,
  },
  data() {
    return {
      name,
      hotel: null,
      images: null,
      facilities: null,
      descriptions: null,
      suppliers: {
        images: null,
        facilities: null,
        descriptions: null,
      },
      activeTab: 'photos',
      languageOptions,
      language: languageOptions.find(({ id }) => id === 'en'),
      supplier: null,
      isLoading: false,
    };
  },
  computed: {
    supplierOptions() {
      let source;
      if (this.activeTab === 'photos') {
        source = this.suppliers.images;
      } else if (this.activeTab === 'facilities') {
        source = this.suppliers.facilities;
      } else if (this.activeTab === 'descriptions') {
        source = this.suppliers.descriptions;
      }
      return Object.keys(source).map(supplier => ({
        title: supplier,
        id: supplier,
      }));
    },
    supplierImages() {
      return this.suppliers.images[this.supplierModel.id];
    },
    supplierDescriptions() {
      return this.suppliers.descriptions[this.supplierModel.id];
    },
    supplierFacilities() {
      return this.suppliers.facilities[this.supplierModel.id];
    },
    supplierModel: {
      get() {
        if (!this.supplier) {
          return this.supplierOptions[0];
        }
        return this.supplierOptions.find(({ id }) => id === this.supplier) || this.supplierOptions[0];
      },
      set({ id }) {
        this.supplier = id;
      },
    },
  },
  methods: {
    random,
    sortBy,
    ...mapActions([
      'getHotelSuppliersImages',
      'getHotelSuppliersFacilities',
      'getHotelSuppliersDescriptions',
    ]),
    getDescription(lang, fromSupplier) {
      const source = fromSupplier ? this.supplierDescriptions : this.descriptions;
      if (Array.isArray(source[lang])) {
        return sortBy(source[lang], ['line']).map(({ paragraph }) => `<p>${paragraph}</p>`).join('');
      } else return source[lang];
    },
    showLightbox(lightbox, index) {
      lightbox.showImage(index);
    },
    close() {
      this.$modal.hide(this.name);
    },
    closeByEscape(e) {
      if (e.keyCode === 27) {
        this.close();
      }
    },
    resetState() {
      this.hotel = null;
      this.images = null;
      this.facilities = null;
      this.descriptions = null;
      this.activeTab = 'photos';
      this.supplier = null;
      this.language = languageOptions.find(({ id }) => id === 'en');
    },
    async beforeOpen(event) {
      this.resetState();

      if (event.params) {
        this.hotel = event.params.hotel;
        this.images = event.params.images;
        this.facilities = event.params.facilities;
        this.descriptions = event.params.descriptions;
        this.activeTab = event.params.activeTab || 'photos';
      }

      const loader = this.$loading.show();
      this.isLoading = true;

      const [
        images,
        facilities,
        descriptions,
      ] = await Promise.all([
        this.getHotelSuppliersImages(this.hotel.hotelId),
        this.getHotelSuppliersFacilities(this.hotel.hotelId),
        this.getHotelSuppliersDescriptions(this.hotel.hotelId),
      ]);
      this.suppliers.images = images;
      this.suppliers.facilities = facilities;
      this.suppliers.descriptions = descriptions;

      loader.hide();
      this.isLoading = false;

      document.addEventListener('keyup', this.closeByEscape);
    },
    beforeClose() {
      document.removeEventListener('keyup', this.closeByEscape);
    },
  },
};
</script>
<style lang="scss">
.hotel-suppliers {
  max-height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
  &__header {
    background: #f7f9fc;
    color: #6e7c91;
    padding: 30px 30px 0;
    flex: 0 0 1%;
    .row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      & + * {margin-top: 15px;}
    }
    .subtitle {
      text-transform: uppercase;
      font-size: 9px;
      line-height: 14px;
    }
    .title {
      font-size: 15px;
      line-height: 20px;
      font-weight: bold;
    }
  }
  &__content {
    flex: 1 1 auto;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .tabs {
    margin-left: -20px;
    font-size: 13px;
    line-height: 16px;
    font-weight: bold;
    text-transform: uppercase;
    &__list {
      display: flex;
    }
    &__item {
      a {
        display: block;
        padding: 13px 20px;
        position: relative;
        text-decoration: none;
        color: currentColor;
        &:after {
          content: '';
          position: absolute;
          height: 4px;
          background: currentColor;
          bottom: -2px;
          border-radius: 2px;
          left: 7px;
          right: 7px;
          opacity: 0;
          transition: opacity 0.15s ease-in-out;
        }
      }
      &.is-active a {
        color: #6972de;
        &:after {opacity: 1;}
      }
    }
  }
  .cols-compare {
    display: flex;
    font-size: 14px;
    line-height: 20px;
    color: #6e7c91;
    &__col {
      width: 50%;
      flex: 0 0 50%;
      padding: 0 12px 0 25px;
      display: flex;
      flex-direction: column;
      .col-title {
        font-weight: bold;
        & + * {margin-top: 20px;}
      }
      .col-content {
        overflow-y: auto;
        padding-right: 12px;
      }
      &:first-child {padding-left: 0;}
      &:last-child {padding-right: 0;}
      & + * {
        border-left: 2px solid #f2f2f2;
      }
    }
  }
}
.hotel-photos {
  max-height: 568px;
  min-height: 187px;
  height: 100%;
  padding: 30px;
  overflow: hidden;
}
.hotel-facilities {
  font-size: 14px;
  line-height: 20px;
  color: #6e7c91;
  display: flex;
  padding: 30px;
  max-height: 474px;
  height: 100%;
  overflow: hidden;
}
.hotel-descriptions {
  font-size: 14px;
  line-height: 20px;
  color: #6e7c91;
  display: flex;
  padding: 30px;
  max-height: 474px;
  min-height: 374px;
  height: 100%;
  overflow: hidden;
  p + p {margin-top: 20px;}
  &.lang-ar {
    .col-content {
      direction: rtl;
    }
  }
}
.tabs-panel {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;

}
</style>

<style lang="scss">
.select-suppliers {
  min-width: 160px;
  &.multiselect {
    min-height: 0;
    cursor: pointer;
  }
  .multiselect {
    &__input {
      min-height: 0;
      font-size: inherit;
      line-height: inherit;
      border-radius: 0;
      margin: 0;
      padding: 0;
      background-color: transparent;
      height: 16px;
    }
    &__tags {
      box-shadow: 0 2px 2.5px rgba(146,171,182,0.07);
      border: none;
      background-color: #fff;
      border-radius: 3px;
      min-height: 0;
      font-size: 14px;
      line-height: 16px;
      font-weight: bold;
      color: #6e7c91;
      padding: 7px 10px;
    }
    &__select {
      width: 30px;
      height: 100%;
      right: 0;
      padding: 0;
      top: 0;
      bottom: 0;
      &:before {
        display: none;
      }
      &:after {
        content: '';
        background-image: url('/icons/icon-arrow-down.svg');
        background-size: contain;
        width: 10px;
        height: 6px;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }
    &__single {
      background: none;
      line-height: inherit;
      margin: 0;
      min-height: 0;
      text-overflow: ellipsis;
      overflow: hidden;
      padding-right: 22px;
    }
    &__content-wrapper {
      min-width: 100%;
      max-width: 100%;
      width: auto;
    }
    &__content {
      display: block !important;
    }
    &__option {
      white-space: normal;
      word-wrap: break-word;
    }
  }
}
.select-language {
  position: absolute;
  top: 26px;
  right: 38px;
  width: auto;
  min-width: 135px;
  &.multiselect {
    min-height: 0;
    cursor: pointer;
    height: 31px;
    color: #6e7c91;
    font-size: 14px;
    display: flex;
    border-radius: 3px;
    background-color: #f7f9fc;
  }
  .option {
    &__image {
      font-size: 0;
      line-height: 0;
      width: 16px;
      height: 16px;
      img {
        display: block;
        width: 100%;
        height: 100%;
      }
    }
    &__title {
      margin-left: 8px;
    }
  }
  .multiselect {
    &__content-wrapper {
      top: 100%;
    }
    &__tags {
      display: flex;
      border: none;
      min-height: 0;
      height: 31px;
      padding: 1px 12px 0;
      border-radius: 0;
      background: none;
      font-weight: bold;
      font-size: 14px;
    }
    &__single {
      cursor: pointer;
      min-height: 0;
      border-radius: 0;
      background: none;
      padding: 0;
      margin-bottom: 0;
      font-size: 14px;
    }
    &__option,
    &__single {
      flex-grow: 1;
      display: flex;
      align-items: center;
    }
    &__select {
      width: 30px;
      height: 100%;
      right: 0;
      padding: 0;
      top: 0;
      bottom: 0;
      &:before {
        display: none;
      }
      &:after {
        content: '';
        background-image: url('/icons/icon-arrow-down.svg');
        background-size: contain;
        width: 10px;
        height: 6px;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }
  }
}
.facilities-group {
  .list {
    columns: 2;
    & > * {
      break-inside: avoid;
      overflow: hidden;
    }
  }
}
</style>
